// @flow
import _ from 'lodash';
import moment from 'moment';
import numeral from 'numeral';
import store from '../../../store';

import {
    OVERVIEW_PANEL_CHANGE_CURRENT,
    OVERVIEW_PANEL_GET_CURRENT,
    OVERVIEW_PANEL_IS_VISIBLE
} from '../../../store/modules/overview-panel/types';
import api from '../../api';
import DataTable from '../../core/charts/Datatable';
import dataBoard from '../../core/databoard';
import filters from '../../core/filters';
import worldMap from '../../core/map';
import translate from '../../core/tools/translate';
import environment from '../../environment';
import layers from '../../layers';
import { CoreControl } from '../CoreControl';
import { VectorType } from '../VectorType';
import { ContextEnum } from '../../../enuns/context.enum';
import { notify, NOTIFY_TYPE } from '../../shared';
import { FeaturePermission } from '../../../constants/feature.permission.helper';
import Storage from '../../storage';

const isAuthorized = (permission, feature) => api.authorization.isAuthorized(permission, feature);

export class CustomersAfterSalesControl extends CoreControl {
    constructor() {
        super({ name: "CustomersAfterSales", context: "LAYER_AFTER_SALES_CUSTOMER" });

        this._change = this._change.bind(this);
        this._changePeriod = this._changePeriod.bind(this);
        this._filterCallback = this._filterCallback.bind(this);
        this._purgeLayer = this._purgeLayer.bind(this);
        this.createFilter = this.createFilter.bind(this);
        this.toggleOverview = this.toggleOverview.bind(this);
        this.currentStores = new Set();

        this.filterValues = {};
    }

    static getInstance() {
        if (!CustomersAfterSalesControl._instance) {
            CustomersAfterSalesControl._instance = new CustomersAfterSalesControl();
        }

        return CustomersAfterSalesControl._instance;
    }

    init() {
        this.selecteds = [];
        this.colors = [
            "black",
            "yellow",
            "blue",
            "orange",
            "purple",
            "red",
            "teal",
            "pink",
            "brown",
            "gray",
        ];

        const values = {
            help: translate(`Help.${this.options.name}`),
            id: "layer-customers",
            input: "checkbox",
            label: translate(`Common.${this.options.name}`),
            layer: this.getLayer,
            change: this._change,
            filter: {
                enable: true,
                click: this.createFilter
            },
            name: this.options.name,
            purge: this._purgeLayer,
            overview: this.toggleOverview,
            type: "vector-points",
            links: {}
        };

        const formatDate = (dt) => moment(dt).format("MMM/YYYY");
        const { start, end } = this._buildInitPeriod();

        values.links[translate("Action.ShowDateFrom")] = {
            id: "layer-customers-date",
            name: "filterYear",
            text: `${formatDate(start)} -> ${formatDate(end)}`,
            value: {
                start: start.toDate(),
                end: end.toDate(),
                max: end.toDate(),
                min: start.toDate()
            },
            change: this._changePeriod,
            type: "datepicker"
        };

        if (!this._instance) {
            this._instance = new VectorType(values);
        }
    }

    _resetCurrentCustomers() {
        this.getLayer().reset();
    }

    _change(e) {
        const storeLayer = layers.get("NetworkAfterSales", worldMap.getCurrent());

        const layer = this.getLayer();

        if (e.checked) {
            layer.redraw();
            layer.reload();
        } else {
            this.currentStores.forEach(store => {
                this._restoreStoreIcon(store, storeLayer);
            });
            layer.hide();
            layer.clear();
            this.currentStores.clear();

            this.selecteds = [];
            storeLayer.state.customIcons = [];
            storeLayer.reload();

            if (!storeLayer.visible()) {
                setTimeout(() => storeLayer.hide(), 500);
            }
        }

        this._setFilterButtonVisibility(e.checked);
    }

    _setFilterButtonVisibility(state) {
        try {
            this.getControls().button.overview.input.enable(state);
            this.getControls().button.overview.enable = state;

            if (
                store.getters[OVERVIEW_PANEL_GET_CURRENT] ===
                this.options.name &&
                !state
            ) {
                dataBoard.hide();
                delete this._overviewFeatures;
            }
        } catch (err) {
            console.log(err);
        }

        state ? this.createFilter() : filters.close();
    }

    _purgeLayer() {
        const layer = this.getLayer();
        const storeLayer = layers.get("NetworkAfterSales", worldMap.getCurrent());

        if (this._instance.check()) {
            this.currentStores.forEach(store => {
                this._restoreStoreIcon(store, storeLayer);
            });
            this._resetCurrentCustomers();
        }

        if (layer.visible()) {
            layer.reload();
        }
    }

    _getIconStyle(id) {
        const color = this.colors.shift();
        this.colors.push(color);
        return {
            store: L.icon({
                iconUrl: `${environment.get('IMAGES_HOST')}/pin_store_customers_${color}.png`,
                iconSize: [50, 64], // size of the icon
                iconAnchor: [25, 60], // point of the icon which will correspond to marker's location
                labelAnchor: [25, -32]
            }),
            customers: {
                value: id,
                style: {
                    color,
                    icon: {
                        iconUrl: `${environment.get('IMAGES_HOST')}/pin_client_${color}.png`,
                        iconSize: [18, 18],
                        iconAnchor: [9, 9]
                    },
                    clickable: false
                }
            }
        };
    }

    _changePeriod(date, id = this.getLayer().getMap()._id) {
        this._date.set(id, {
            start: moment(date.start),
            end: moment(date.end),
            min: new Date(date.min),
            max: new Date(date.max)
        });

        if (this._instance.check()) {
            this.add([...this.currentStores]);
        }
    }

    get layerPeriod() {
        const id = this.getLayer().getMap()._id;
        return (this).date.get(id);
    }

    _setStoreIcon(geometries, storeLayer) {
        const { symbology } = this.getLayer().options;

        const stores = _(geometries)
            .map(x => x.properties.sid)
            .uniq()
            .value();

        stores.forEach(storeId => {
            const style = this._getIconStyle(storeId);
            this.currentStores.add(storeId);

            storeLayer.changeIcon(storeId, style.store);

            this.setHaloSelected(storeId);

            storeLayer.state.customIcons = [
                ...storeLayer.state.customIcons.filter(x => x.idStore !== storeId),
                { idStore: storeId, icon: style.store }];

            symbology.values.push(style.customers);

            this.getLayer().setOptions({
                symbology
            });
        });
    }

    _restoreStoreIcon(id, storeLayer) {
        const layer = this.getLayer();

        storeLayer.state.customIcons = [...storeLayer.state.customIcons.filter(x => x.idStore !== id)];

        storeLayer.restoreIcon(id);

        const { symbology } = layer.options;
        symbology.values = symbology.values.filter(s => String(s.value) !== String(id));

        layer.setOptions({ symbology });
    }

    _filterCallback(values) {
        this.check(true);
        this._purgeLayer();

        this.filterValues = {...values};

        this.add([]);
    }

    createFilter() {
        filters.create({
            id: `${_.kebabCase(this.options.name)}-filter-id`,
            label: translate(`Common.${this.options.name}`),
            type: _.lowerCase(this.options.name),
            onSubmit: this._filterCallback,
            fields: {
                stateAcronym: true,
                salesCustomerTypes: true
            },
        });
    }

    _buildInitPeriod(id = this.getLayer().getMap()._id) {
        const datePrincipal = this.getDate();

        if (isAuthorized(FeaturePermission.LAYER_AFTER_SALES_CUSTOMER.filterLast12Months)) {
            this._date.set(id, {
                start: moment(datePrincipal.end.clone().subtract(11, 'months'),),
                end: moment(datePrincipal.end.clone())
            });
            return {
                start: datePrincipal.end.clone().subtract(11, 'months'),
                end: datePrincipal.end.clone()
            };
        }

        return {
            start: datePrincipal.start.clone(),
            end: datePrincipal.end.clone()
        };
    }

    getDate(id = this.getLayer().getMap()._id) {
        const period = environment.get('CONFIG.FILTERS.CUSTOMERS_PERIOD');

        if (_.isUndefined(this._date)) {
            this._date = new Map();
        }

        if (!this._date.has(id)) {

            if (id === 0)
                this._date.set(id, { start: moment(period.periodInitialFormatted, "YYYY-MM-DD"), end: moment(period.periodEndFormatted, "YYYY-MM-DD") });
            else
                this._date.set(id, this._date.get((id - 1)));
        }

        return this._date.get(id);
    }

    toggleOverview() {
        const currentMap = worldMap.getCurrent();
        const layer = this.getLayer(currentMap._id);
        const features = layer.getCurrentFeatures();

        if (!store.getters[OVERVIEW_PANEL_IS_VISIBLE] || !_.isEqual(this._overviewFeatures, features))

        if ((_.size(features) && !_.isEqual(this._overviewFeatures, features)) || !store.getters[OVERVIEW_PANEL_IS_VISIBLE]) {
            this.summary(features);
            this._overviewFeatures = _.cloneDeep(features);

            store.dispatch(OVERVIEW_PANEL_CHANGE_CURRENT, this.options.name);
        }
    }

    getButtonExport() {
        const date = this.getDate();
        const filterVal = this.filterValues;
        const storesFil = this.currentStores;
        const exportCustomButton = {
            icon: 'download',
            text: 'custom',
            name: translate('Action.Export'),
            callback: function () {
                const parameters = {
                    yearInitial: date.start.format("YYYY"),
                    monthInitial: date.start.format("MM"),
                    yearEnd: date.end.format("YYYY"),
                    monthEnd: date.end.format("MM"),
                    storeIds: storesFil,
                    salesCustomerTypeIds: filterVal.salesTypes
                        ? filterVal.salesTypes.value
                        : null,
                    };
                api.afterSales.get.customers.exportSummaryCustom(parameters);
            }
        };

        return exportCustomButton;
    }

    summary(features) {
        const data = this.calculate(features);

        const chartPie = new DataTable(data.perPercentage, {
            row: translate("Common.Name"),
            column: [translate("Common.Total")]
        });

        const chartColumn = new DataTable(data.perTotal, {
            row: translate("Common.Name"),
            column: [translate("Common.Total")]
        });

        const options = {
            title: {
                table: `${translate(`Common.${this.options.name}`)} - Sumário`,
                chartPie: `TOP 10 - ${translate("Common.Store")} (%)`,
                chartColumn: `TOP 10 - ${translate("Common.Store")}`
            },
            dash: true,
            colors: [
                null,
                _.map(_.range(0, data.perTotal.length), () => "#3498db")
            ],
            data: [data.summary, chartPie, chartColumn],
            convertTable: false,
            format: ["{0:n0}", "{0:p2}", "{0:n0}"],
            columns: [
                {
                    field: "name",
                    title: translate("Common.Name")
                },
                {
                    field: "total",
                    title: translate("Common.Total"),
                    width: 100,
                    template: data => numeral(data.total).format("0,0")
                },
                {
                    field: "percentage",
                    title: `${translate("Common.Total")} (%)`,
                    width: 100,
                    template: data =>
                        numeral(data.percentage).format("0,0[.]00%")
                }
            ],
            buttons: {
                fullscreen: true,
                custom: isAuthorized(FeaturePermission[this.options.context].exportDataPanel)
                    ? this.getButtonExport(this.options)
                    : undefined,
            }
        };

        dataBoard.show(options);
    }

    calculate(features) {
        const stores =  _(features)
        .map(x => ({
            storeId: x.properties.sid,
            name: x.properties.n,
            group: x.properties.n
        }))
        .uniqBy("storeId")
        .reduce((acc, value) => {
            acc[value.storeId] = {
                name: value.name,
                group: value.group,
            };
            return acc;
        }, {});

        const total = features.length;

        const summary = _(features)
            .groupBy(f => f.properties.sid)
            .map((val, key) => ({
                name: stores[key].name,
                group: stores[key].group,
                total: val.length,
                percentage: val.length / total
            }))
            .orderBy("total")
            .reverse()
            .value();

        const perPercentage = _(features)
            .groupBy(f => f.properties.sid)
            .map((val, key) => ({
                [translate("Common.Name")]: stores[key].name,
                [translate("Common.Total")]: val.length / total
            }))
            .orderBy(translate("Common.Total"))
            .reverse()
            .take(10)
            .value();

        const perTotal = _(features)
            .groupBy(f => f.properties.sid)
            .map((val, key) => ({
                [translate("Common.Name")]: stores[key].name,
                [translate("Common.Total")]: val.length
            }))
            .orderBy(translate("Common.Total"))
            .reverse()
            .take(10)
            .value();

        return {
            perPercentage,
            perTotal,
            summary
        };
    }

    setHaloSelected(idLoja) {
        this.selecteds = [...this.selecteds, idLoja];
    }

    removeHaloSelected(idLoja) {
        this.selecteds = this.selecteds.filter(x => x !== idLoja);
    }

    isHaloSelected(idLoja) {
        return this.selecteds.some(x => x === idLoja);
    }

    add(
        storeIds,
        storeLayer,
        autoReload = true
    ) {
        const date = this.getDate();
        const layer = this.getLayer();
        const { symbology } = this.getLayer().options;

        layer.clear();
        symbology.values = [];

        if (!storeLayer) {
            storeLayer = layers.get("NetworkAfterSales", worldMap.getCurrent());
        }

        if (!storeLayer.state.customIcons) {
            storeLayer.state.customIcons = [];
        }

        if (!_.isArray(storeIds)) {
            this.currentStores.add(storeIds);
        }

        if (!this.currentStores.size) {
            notify(NOTIFY_TYPE.LOG, "Habilite pelo menos uma loja para visualizar os clientes");
            return;
        }

        api.fetch({
            type: "POST",
            url: `${environment.get("API_HOST")}/aftersales/customers`,
            data: JSON.stringify({
                yearInitial: date.start.format("YYYY"),
                monthInitial: date.start.format("MM"),
                yearEnd: date.end.format("YYYY"),
                monthEnd: date.end.format("MM"),
                storeIds: this.currentStores,
                salesCustomerTypeIds: this.filterValues.salesTypes
                    ? this.filterValues.salesTypes.value
                    : null,
            })
        })
        .then(res => {
            if (!res.objects.output) {
                notify(NOTIFY_TYPE.LOG, translate("Message.NotFounded", { resource: "$t(Common.Client)" }));
                layer.setFeatures([], autoReload);
                return;
            }

            this._setStoreIcon(res.objects.output.geometries, storeLayer);

            layer.setFeatures(res, autoReload);
        });
    }

    remove(id, storeLayer, autoReload = true) {
        const layer = this.getLayer();
        if (!storeLayer) {
            storeLayer = layers.get("NetworkAfterSales", worldMap.getCurrent());
        }

        this._restoreStoreIcon(id, storeLayer);

        _.forEach(layer.getFeatures(), feature => {
            if (feature && feature.properties.sid === id) {
                layer.removeLayer(feature.properties.Id, false);
            }
        });

        this.currentStores.delete(id);

        if (layer.visible() && autoReload) {
            setTimeout(() => layer.reload(), 500);
        }
    }
}

export default CustomersAfterSalesControl;



// WEBPACK FOOTER //
// ./src/scripts/controls/AfterSales/CustomersAfterSalesControl.js